import { useEffect, useState } from 'react';
import { ImagePlus, Loader2, Pencil, Trash2, X, Eye, EyeOff, ArrowUp, ArrowDown, Smartphone } from 'lucide-react';
import { apiFetch } from '../lib/api';
import MediaLibraryPicker from '../components/admin/MediaLibraryPicker';

interface AhJimIllustration {
  id: number;
  title: string;
  imageUrl: string;
  description: string;
  quote: string;
  category: string;
  insight: string;
  sortOrder: number;
  isActive: number;
  createdAt: string;
}

const emptyForm = {
  title: '',
  imageUrl: '',
  quote: '',
  category: '',
  insight: '',
  sortOrder: 0,
  isActive: 1,
};

export default function AdminAhJimIllustrations() {
  const [items, setItems] = useState<AhJimIllustration[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    const res = await apiFetch<AhJimIllustration[]>('/api/admin/ahjim-illustrations');
    if (res.success && res.data) {
      setItems(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sortOrder: items.length + 1 });
    setModalOpen(true);
  };

  const openEdit = (item: AhJimIllustration) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      imageUrl: item.imageUrl,
      quote: item.quote || '',
      category: item.category || '',
      insight: item.insight || '',
      sortOrder: item.sortOrder,
      isActive: item.isActive,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      alert('請輸入標題');
      return;
    }
    if (!form.imageUrl.trim()) {
      alert('請選擇或輸入圖片URL');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      sortOrder: Number(form.sortOrder),
    };
    const url = editingId
      ? `/api/admin/ahjim-illustrations/${editingId}`
      : '/api/admin/ahjim-illustrations';
    const method = editingId ? 'PUT' : 'POST';
    const res = await apiFetch(url, {
      method,
      body: JSON.stringify(payload),
    });
    setSaving(false);
    if (res.success) {
      setModalOpen(false);
      fetchItems();
    } else {
      alert(res.error?.message || '保存失敗');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定要刪除這個插圖嗎？')) return;
    const res = await apiFetch(`/api/admin/ahjim-illustrations/${id}`, { method: 'DELETE' });
    if (res.success) {
      fetchItems();
    } else {
      alert(res.error?.message || '刪除失敗');
    }
  };

  const toggleActive = async (item: AhJimIllustration) => {
    const res = await apiFetch(`/api/admin/ahjim-illustrations/${item.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: item.isActive ? 0 : 1 }),
    });
    if (res.success) fetchItems();
  };

  const handleReorder = async (item: AhJimIllustration, direction: 'up' | 'down') => {
    const currentIndex = items.findIndex((i) => i.id === item.id);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    const targetItem = items[targetIndex];

    // 交換 sortOrder
    await apiFetch(`/api/admin/ahjim-illustrations/${item.id}`, {
      method: 'PUT',
      body: JSON.stringify({ sortOrder: targetItem.sortOrder }),
    });
    await apiFetch(`/api/admin/ahjim-illustrations/${targetItem.id}`, {
      method: 'PUT',
      body: JSON.stringify({ sortOrder: item.sortOrder }),
    });

    fetchItems();
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Smartphone className="w-6 h-6 text-brand-600" />
            阿占隨意插圖庫
          </h1>
          <button onClick={openCreate} className="btn-primary py-2.5 px-5 min-h-[44px]">
            <ImagePlus className="w-4 h-4" />
            新增插圖
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          管理阿占回帖卡片的 DIY 手機殼插圖，捐款滿 $500 的捐贈者將隨機獲得一款
        </p>
      </div>

      {/* Grid */}
      <div className="card shadow-sm border border-brand-100">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
          </div>
        ) : items.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
            {items.map((item, index) => (
              <div
                key={item.id}
                className={`group relative bg-white rounded-xl border overflow-hidden transition-all hover:shadow-md ${
                  item.isActive ? 'border-gray-200' : 'border-gray-200 opacity-60'
                }`}
              >
                {/* Image */}
                <div className="aspect-square bg-gray-100 relative overflow-hidden">
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/placeholder-ahjim-1.jpg';
                    }}
                  />
                  {!item.isActive && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <span className="px-3 py-1 bg-black/60 text-white text-xs font-bold rounded-full">
                        已隱藏
                      </span>
                    </div>
                  )}
                  {/* Hover actions */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEdit(item)}
                      className="p-1.5 bg-white/90 hover:bg-white text-gray-700 rounded-lg shadow-sm transition-colors"
                      title="編輯"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-1.5 bg-white/90 hover:bg-white text-red-600 rounded-lg shadow-sm transition-colors"
                      title="刪除"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="p-3">
                  <h3 className="font-bold text-sm text-gray-900 truncate">{item.title}</h3>
                  {item.quote && (
                    <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{item.quote}</p>
                  )}
                  <div className="flex items-center gap-1.5 mt-1.5">
                    {item.category && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-brand-50 text-brand-700 rounded-full font-medium">
                        {item.category}
                      </span>
                    )}
                    {item.insight && (
                      <span className="text-[10px] text-gray-400 truncate">{item.insight}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleActive(item)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          item.isActive
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={item.isActive ? '顯示中' : '已隱藏'}
                      >
                        {item.isActive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      </button>
                      <span className="text-xs text-gray-400">排序 {item.sortOrder}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => handleReorder(item, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                        title="上移"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleReorder(item, 'down')}
                        disabled={index === items.length - 1}
                        className="p-1 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                        title="下移"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Smartphone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">暫無插圖，點擊「新增插圖」開始添加</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {editingId ? '編輯插圖' : '新增插圖'}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">作品名稱 *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="例如：人生大便"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">圖片 *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={form.imageUrl}
                    onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
                    className="flex-1 p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                    placeholder="輸入圖片 URL 或從媒體庫選擇"
                  />
                  <button
                    onClick={() => setPickerOpen(true)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors"
                  >
                    媒體庫
                  </button>
                </div>
                {form.imageUrl && (
                  <div className="mt-2 w-24 h-24 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                    <img
                      src={form.imageUrl}
                      alt="預覽"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/images/placeholder-ahjim-1.jpg';
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">金句（Caption）</label>
                <textarea
                  value={form.quote}
                  onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
                  rows={2}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="輸入金句..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">類別</label>
                  <input
                    type="text"
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                    placeholder="例如：💔 扎心共鳴"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">解畫（Insight）</label>
                  <input
                    type="text"
                    value={form.insight}
                    onChange={(e) => setForm((f) => ({ ...f, insight: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                    placeholder="輸入解畫..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">排序</label>
                  <input
                    type="number"
                    min={0}
                    value={form.sortOrder}
                    onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <div className="flex items-center gap-2 pb-3">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={!!form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked ? 1 : 0 }))}
                      className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                    />
                    <label htmlFor="isActive" className="text-sm text-gray-700">
                      前台顯示
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="btn-secondary py-2.5 px-5 min-h-[44px]"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary py-2.5 px-5 min-h-[44px]"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Library Picker */}
      <MediaLibraryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => setForm((f) => ({ ...f, imageUrl: url }))}
        filterType="image"
      />
    </div>
  );
}
